import React, { useState, useEffect, useMemo } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Plane, ArrowRight, Edit3, Loader2, Calendar, ArrowLeftRight, Sparkles } from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { Money } from '../contexts/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FareCalendarGrid = ({ 
  origin, 
  destination, 
  departureDate, 
  returnDate,
  searchParams,
  onSelectDates,
  onModifySearch,
  onBack
}) => {
  const [fareMatrix, setFareMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCell, setSelectedCell] = useState(null);

  // Generate 7 dates: selected date ± 3 days
  const departureDates = useMemo(() => {
    if (!departureDate) return [];
    const centerDate = new Date(departureDate);
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDays(subDays(centerDate, 3), i);
      return format(date, 'yyyy-MM-dd');
    });
  }, [departureDate]);

  const returnDates = useMemo(() => {
    if (!returnDate) return [];
    const centerDate = new Date(returnDate);
    return Array.from({ length: 7 }, (_, i) => {
      const date = addDays(subDays(centerDate, 3), i);
      return format(date, 'yyyy-MM-dd');
    });
  }, [returnDate]);

  // Fetch fare data
  useEffect(() => {
    const fetchFareMatrix = async () => {
      if (!origin || !destination || departureDates.length === 0 || returnDates.length === 0) {
        return;
      }

      setLoading(true);

      try {
        const response = await fetch(`${API_URL}/api/flights/fare-matrix`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            origin,
            destination,
            departure_dates: departureDates,
            return_dates: returnDates
          })
        });

        const data = await response.json();
        
        if (data.success) {
          setFareMatrix(data.matrix);
        } else {
          generateFallbackMatrix();
        }
      } catch (err) {
        console.error('Error fetching fare matrix:', err);
        generateFallbackMatrix();
      } finally {
        setLoading(false);
      }
    };

    const generateFallbackMatrix = () => {
      const matrix = {};
      const baseFare = 480 + Math.random() * 180;

      departureDates.forEach(depDate => {
        returnDates.forEach(retDate => {
          if (retDate > depDate) {
            const key = `${depDate}_${retDate}`;
            const dayDiff = Math.abs(new Date(retDate) - new Date(depDate)) / (1000 * 60 * 60 * 24);
            
            let fare = baseFare;
            const depDay = new Date(depDate).getDay();
            const retDay = new Date(retDate).getDay();
            if (depDay === 0 || depDay === 6) fare += 55;
            if (retDay === 0 || retDay === 6) fare += 55;
            if (dayDiff > 5) fare -= 35;
            fare += (Math.random() - 0.5) * 140;
            if (Math.random() < 0.08) fare = baseFare * 0.6;
            fare = Math.max(fare, 320);
            
            matrix[key] = Math.round(fare);
          }
        });
      });

      setFareMatrix(matrix);
    };

    fetchFareMatrix();
  }, [origin, destination, departureDates, returnDates]);

  // Calculate price statistics
  const priceStats = useMemo(() => {
    const prices = Object.values(fareMatrix).filter(p => p != null);
    if (prices.length === 0) return { min: 0, max: 0 };
    
    const sorted = [...prices].sort((a, b) => a - b);
    return { 
      min: sorted[0], 
      max: sorted[sorted.length - 1],
      range: sorted[sorted.length - 1] - sorted[0]
    };
  }, [fareMatrix]);

  // Get opacity based on price (darker = more expensive, lighter = cheaper)
  const getPriceIntensity = (price) => {
    if (!price || priceStats.range === 0) return 0.3;
    const normalized = (price - priceStats.min) / priceStats.range;
    // Invert: lower price = lower intensity (lighter), higher price = higher intensity (darker)
    return 0.15 + (normalized * 0.45); // Range from 0.15 to 0.6
  };

  // Format date for display
  const formatDateShort = (dateStr) => {
    const date = new Date(dateStr);
    return {
      day: format(date, 'EEE'),
      date: format(date, 'd'),
      month: format(date, 'MMM')
    };
  };

  const formatDateFull = (dateStr) => {
    const date = new Date(dateStr);
    return format(date, 'EEE, d MMM yyyy');
  };

  // Handle cell click
  const handleCellClick = (depDate, retDate, price) => {
    if (!price) return;
    setSelectedCell({ depDate, retDate, price });
  };

  // Handle search for selected dates
  const handleSearchDates = () => {
    if (!selectedCell) return;
    onSelectDates(selectedCell.depDate, selectedCell.retDate);
  };

  // Check if this is the original selected date
  const isOriginalDate = (depDate, retDate) => {
    const origDep = format(new Date(departureDate), 'yyyy-MM-dd');
    const origRet = format(new Date(returnDate), 'yyyy-MM-dd');
    return depDate === origDep && retDate === origRet;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col items-center justify-center gap-6 py-20">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-slate-700 rounded-full"></div>
              <div className="w-20 h-20 border-4 border-t-white rounded-full animate-spin absolute top-0"></div>
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-light text-white">Loading Fare Calendar</h3>
              <p className="text-slate-400 mt-2">Finding the best prices for your trip...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-6xl">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-light text-white">Fare Calendar</h1>
            </div>
            <div className="flex items-center gap-2 text-slate-400">
              <span className="text-white font-medium">{origin}</span>
              <ArrowLeftRight className="h-4 w-4" />
              <span className="text-white font-medium">{destination}</span>
              <span className="text-slate-500 mx-2">•</span>
              <span>±3 days flexibility</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              onClick={onBack}
              variant="ghost"
              className="text-slate-400 hover:text-white hover:bg-white/10"
            >
              ← Back
            </Button>
            <Button 
              onClick={onModifySearch}
              className="bg-white text-slate-900 hover:bg-slate-100"
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Modify
            </Button>
          </div>
        </div>

        {/* Price Range Indicator */}
        <div className="flex items-center justify-between mb-6 px-1">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-400" />
              <span className="text-emerald-400 font-medium">Best: <Money gbp={priceStats.min} /></span>
            </div>
            <div className="hidden sm:flex items-center gap-3">
              <div className="h-2 w-32 bg-gradient-to-r from-slate-700/30 to-slate-700 rounded-full"></div>
              <span className="text-slate-500 text-sm">Higher prices are darker</span>
            </div>
          </div>
          <span className="text-slate-500">Max: <Money gbp={priceStats.max} /></span>
        </div>

        {/* Fare Grid */}
        <Card className="overflow-hidden bg-slate-800/50 border-slate-700/50 backdrop-blur-sm mb-6">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr>
                  <th className="p-4 text-left">
                    <div className="text-xs text-slate-500 uppercase tracking-wider">
                      <div className="flex items-center gap-1.5">
                        <Plane className="h-3 w-3 -rotate-45" />
                        Outbound
                      </div>
                      <div className="text-slate-600 mt-1">Return →</div>
                    </div>
                  </th>
                  {returnDates.map((retDate) => {
                    const dateInfo = formatDateShort(retDate);
                    const isWeekend = new Date(retDate).getDay() === 0 || new Date(retDate).getDay() === 6;
                    return (
                      <th key={retDate} className="p-2 text-center min-w-[80px]">
                        <div className={`${isWeekend ? 'text-amber-400/80' : 'text-slate-400'}`}>
                          <div className="text-[10px] uppercase tracking-wider opacity-70">{dateInfo.day}</div>
                          <div className="text-lg font-light">{dateInfo.date}</div>
                          <div className="text-[10px] uppercase tracking-wider opacity-70">{dateInfo.month}</div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {departureDates.map((depDate) => {
                  const dateInfo = formatDateShort(depDate);
                  const isWeekend = new Date(depDate).getDay() === 0 || new Date(depDate).getDay() === 6;
                  return (
                    <tr key={depDate} className="border-t border-slate-700/30">
                      <td className="p-3">
                        <div className={`${isWeekend ? 'text-amber-400/80' : 'text-slate-400'}`}>
                          <div className="text-[10px] uppercase tracking-wider opacity-70">{dateInfo.day}</div>
                          <div className="text-lg font-light">{dateInfo.date}</div>
                          <div className="text-[10px] uppercase tracking-wider opacity-70">{dateInfo.month}</div>
                        </div>
                      </td>
                      {returnDates.map((retDate) => {
                        const key = `${depDate}_${retDate}`;
                        const price = fareMatrix[key];
                        const isSelected = selectedCell?.depDate === depDate && selectedCell?.retDate === retDate;
                        const isOriginal = isOriginalDate(depDate, retDate);
                        const isValidDate = retDate > depDate;
                        const isLowestPrice = price === priceStats.min;
                        const intensity = getPriceIntensity(price);

                        if (!isValidDate) {
                          return (
                            <td key={key} className="p-1.5">
                              <div className="h-16 flex items-center justify-center">
                                <div className="w-6 h-px bg-slate-700/50"></div>
                              </div>
                            </td>
                          );
                        }

                        return (
                          <td key={key} className="p-1.5">
                            <div
                              onClick={() => handleCellClick(depDate, retDate, price)}
                              className={`
                                h-16 rounded-lg flex flex-col items-center justify-center cursor-pointer
                                transition-all duration-200 relative group
                                ${isSelected 
                                  ? 'bg-white text-slate-900 shadow-lg shadow-white/20 scale-105' 
                                  : isLowestPrice
                                    ? 'bg-emerald-500/20 hover:bg-emerald-500/30 ring-1 ring-emerald-500/50'
                                    : 'hover:scale-105 hover:bg-slate-600/50'
                                }
                                ${isOriginal && !isSelected ? 'ring-1 ring-white/30' : ''}
                              `}
                              style={!isSelected && !isLowestPrice ? { 
                                backgroundColor: `rgba(100, 116, 139, ${intensity})` 
                              } : {}}
                            >
                              {price ? (
                                <>
                                  <span className={`font-medium text-sm ${
                                    isSelected ? 'text-slate-900' : 
                                    isLowestPrice ? 'text-emerald-300' : 'text-white'
                                  }`}>
                                    <Money gbp={price} />
                                  </span>
                                  {isLowestPrice && !isSelected && (
                                    <span className="text-[9px] text-emerald-400 font-medium mt-0.5 flex items-center gap-0.5">
                                      <Sparkles className="h-2.5 w-2.5" />
                                      BEST
                                    </span>
                                  )}
                                  {isOriginal && !isSelected && !isLowestPrice && (
                                    <span className="text-[9px] text-slate-400 mt-0.5">Current</span>
                                  )}
                                </>
                              ) : (
                                <span className="text-slate-600">—</span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Selected Date Panel */}
        {selectedCell && (
          <Card className="p-6 bg-white border-0 mb-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center">
                  <Plane className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-slate-500 text-sm">Selected Trip</p>
                  <p className="text-slate-900 font-medium">
                    {formatDateFull(selectedCell.depDate)} → {formatDateFull(selectedCell.retDate)}
                  </p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    <Money gbp={selectedCell.price} />
                    <span className="text-sm font-normal text-slate-500 ml-2">round trip</span>
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedCell(null)}
                  className="border-slate-200"
                >
                  Clear
                </Button>
                <Button 
                  onClick={handleSearchDates}
                  className="bg-slate-900 hover:bg-slate-800 text-white px-8"
                >
                  Search Flights
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Footer Note */}
        <p className="text-center text-slate-500 text-sm">
          Prices shown are estimated combined fares. Weekend dates are highlighted in amber.
        </p>
      </div>
    </div>
  );
};

export default FareCalendarGrid;
